/**
 * How the mirrored Bexio invoices are distributed across statuses.
 *
 * Straight from `status_counts`, which the service builds over ALL of the org's
 * mirrored invoices — not over the counted set. That difference matters enough to
 * state on the card: `draft`, `canceled` and `unknown` invoices appear here but
 * contribute to none of the money figures above, which is exactly why an operator
 * wants to see them.
 *
 * A bar per status rather than a donut: the widths are shares of a count the
 * response gives directly, so nothing has to be estimated, and each row still
 * carries its own number for a reader who cannot compare lengths.
 */
import React from "react";
import { PieChart } from "lucide-react";

import { INVOICE_STATUS_LABEL, mapLabel } from "../bexioFormat";

/** Fixed order, so the card does not reshuffle as counts change. */
const ORDER = ["pending", "partial", "paid", "draft", "canceled", "unknown"];

/** Which statuses the reconciliation counts; the rest are excluded from totals. */
const COUNTED = new Set(["pending", "partial", "paid"]);

const BAR = {
  pending: "bg-amber-500/70",
  partial: "bg-amber-500/70",
  paid: "bg-emerald-500/70",
  draft: "bg-muted-foreground/40",
  canceled: "bg-red-500/60",
  unknown: "bg-muted-foreground/40",
};

export default function BexioStatusMixCard({ statusCounts }) {
  const counts = statusCounts || {};
  const rows = ORDER.map((k) => ({ key: k, count: Number(counts[k] || 0) })).filter(
    (r) => r.count > 0
  );
  const total = rows.reduce((a, r) => a + r.count, 0);

  return (
    <section
      aria-labelledby="bexio-status-mix-title"
      className="rounded-xl border border-border bg-card p-5"
    >
      <div className="mb-1 flex items-center gap-2">
        <PieChart aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        <h3
          id="bexio-status-mix-title"
          className="text-sm font-semibold text-foreground"
        >
          Bexio-Status aller Rechnungen
        </h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Alle gespiegelten Rechnungen — auch nicht zugeordnete. Nur ausstehend,
        teilweise bezahlt und bezahlt fließen in die Summen oben ein.
      </p>

      {total === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Rechnungen gespiegelt.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.key}>
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 truncate text-foreground">
                  {mapLabel(INVOICE_STATUS_LABEL, r.key)}
                  {!COUNTED.has(r.key) ? (
                    <span className="ml-1 text-xs text-muted-foreground">
                      (nicht gezählt)
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 tabular-nums font-medium text-foreground">
                  {r.count}
                </span>
              </div>
              <div
                className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted"
                aria-hidden="true"
              >
                <div
                  className={`h-full rounded-full ${BAR[r.key] || BAR.unknown}`}
                  style={{ width: `${(r.count / total) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
